import React, { useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession } from 'aws-amplify/auth';
import { generateClient } from '@aws-amplify/api';
import useModel from '@/hooks/useModel';
import RootContext from '@/RootContext';
import Footer from './Footer';
import ErrorModal from '@/ErrorModal';
import Privacy from '@/Privacy';
import SignInPage from '@/login';
import Sidebar from './Sidebar';
import Home from './pages/Home';
import CustomerEngagement from './pages/CustomerEngagement';
import AIAssistant from './pages/AIAssistant';
import EnrollmentPipeline from './pages/EnrollmentPipeline';
import Analytics from './pages/Analytics';
import SidebarContext from '@/SidebarContext';
import { getRecruitmentSources as _getRecruitmentSources, getStudies as _getStudies, getSubjects as _getSubjects, getSubjectDealStatusCounts as _getSubjectDealStatusCounts, getSubjectEnrollmentStatusCounts as _getSubjectEnrollmentStatusCounts } from '@/graphql/queries';
import Studies from './pages/Studies';
import Campaigns from './pages/Campaigns';
import DataSegments from './pages/DataSegments';
import Sites from './pages/Sites';
import RecruitmentSources from './pages/RecruitmentSources';
import Waitlists from './pages/Waitlists';
import CampaignList from './pages/CampaignList';
import Subjects from './pages/Subjects';
import AgentActivityExpanded from './Features/expanded-widgets-view/AgentActivityExpanded';
import CallAnalysisExpanded from './Features/expanded-widgets-view/CallAnalysisExpanded';
import EngagementSummaryExpanded from './Features/expanded-widgets-view/EngagementSummaryExpanded';
import AgentActionQueueExpanded from './Features/expanded-widgets-view/AgentActionQueueExpanded';
import TopPerformingOutreachMethodsExpanded from './Features/expanded-widgets-view/TopPerformingOutreachMethodsExpanded';
import BookedRevenueExpanded from './Features/expanded-widgets-view/BookedRevenueExpanded';
import Workflows from './pages/Workflows';
import FlowExpandedView from './WorkflowEntry/FlowExpandedView';
import UserAccountPage from './userAccount/UserAccountPage';
import ChatbotStudio from './chatbot_embed/ChatbotStudio';
import { getCookie, removeCookie, setCookie } from '@/utils';

const client = generateClient();

const getDealStatuses = async ({ context }) => {
	if (context.deal_statuses) return context.deal_statuses;
	return client.graphql({ query: _getSubjectDealStatusCounts })
		.then(response => {
			context.set('deal_statuses', response.data.getSubjectDealStatusCounts.subject_deal_status_counts.map(e => ({ id: e.status, priority: e.priority })));
		});
};

const getEnrollmentStatuses = async ({ context }) => {
	if (context.enrollment_statuses) return context.enrollment_statuses;
	return client.graphql({ query: _getSubjectEnrollmentStatusCounts })
		.then(response => {
			context.set('enrollment_statuses', response.data.getSubjectEnrollmentStatusCounts.subject_enrollment_status_counts.map(e => ({ id: e.status, priority: e.priority })));
		});
};

const stripSearch = search => {
	const queryParams = new URLSearchParams(search);
	queryParams.delete('tab');
	return queryParams.toString();
};

const ExternalRedirect = ({ href }) => {
	useEffect(() => {
		window.location.href = href;
	}, []);
	return <p>Redirecting...</p>;
};

export default () => {
	const rootContext = useModel({});
	const location = useLocation();
	const navigate = useNavigate();
	const [busy, setBusy] = useState(true);
	const [errorMessage, setErrorMessage] = useState();

	const auth = rootContext.get('auth');
	const [isOpen, setIsOpen] = useState(false);
	useEffect(() => {
		fetchAuthSession()
			.then(user => {
				// Get rid of the code in the redirect from Google
				const search = new URLSearchParams(location.search);
				search.delete('code');
				search.delete('state');
				navigate({ search: search.toString() }, { replace: true });
				const idToken = user.tokens.idToken;
				const accessToken = user.tokens.accessToken;
				rootContext.set('auth', {
					family_name: idToken.payload.family_name,
					given_name: idToken.payload.given_name,
					email: idToken.payload.email,
					username: accessToken.payload.username,
					groups: idToken.payload['cognito:groups'],
					idToken: idToken.toString()
				});
				return user;
			})
			.then(user => Promise.allSettled([getDealStatuses({ context: rootContext }), getEnrollmentStatuses({ context: rootContext })]))
			.then(user => {
				setBusy(false);
			})
			.catch(error => {
				// Parse the error_description if any
				const search = new URLSearchParams(location.search);
				const error_description = search.get('error_description');
				const parts = error_description ? error_description.replace(/\+/g, ' ').split('PreSignUp failed with error User not approved') : [];
				if (parts.length === 2) {
					setErrorMessage('User not approved');
				} else {
					setErrorMessage(error_description);
				}
				setBusy(false);
				search.delete('error');
				search.delete('error_description');
				search.delete('state');
				if (window.location.pathname.startsWith('/user-account')) {
					// Forcing to this default path after sign in redirect
					setCookie('DEFAULT_URL', window.location.pathname, 10);
				}
				navigate({ search: search.toString() }, { replace: true });
			});
		// Let the Hub module listen on Auth events
		const listener = Hub.listen('auth', data => {
			switch (data.payload.event) {
				case 'signIn':
					// Retrieve currently signed in user
					fetchAuthSession()
						.then(user => {
							const idToken = user.tokens.idToken;
							rootContext.set('auth', {
								family_name: idToken.payload.family_name,
								given_name: idToken.payload.given_name,
								groups: idToken.payload['cognito:groups'],
								idToken: idToken.toString()
							});
						})
						.catch(() => { });
					break;
				case 'signOut':
				case 'signedOut':
					// Depending on signin method it may cause a page refresh so we want to debounce the UI
					setTimeout(() => rootContext.set('auth', undefined), 1000);
					break;
				case 'signIn_failure':
					const parts = data.payload.data.message ? decodeURIComponent(data.payload.data.message).replace(/\+/g, ' ').split('PreSignUp failed with error User not approved') : [];
					if (parts.length === 2) {
						/*
						this.toggle('formUserNotApprovedOptions')({
							onClosed: () => {
								this.props.history.push('/');
							}
						});
						*/
					}
					break;
				default:
					break;
			}
		});
		return () => Hub.remove('auth', listener);
	}, []);

	if (busy) return null;

	return (
		<SidebarContext.Provider value={{ isOpen, setIsOpen }}>
			<RootContext.Provider value={rootContext}>
				{errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage()} title="Error" />}
				<div className="d-flex min-vh-100">
					{auth && <Sidebar />} {/* Sidebar only when authenticated */}

					<div
						className="flex-grow-1 d-flex flex-column"
						style={{
							marginLeft: auth ? (isOpen ? '240px' : '60px') : '0',
							transition: 'margin-left 0.3s ease'
						}}
					>
						<main className="flex-grow-1">
							<div className="app h-100">
								{!auth ? (
									<Routes>
										<Route path="privacy" element={<Privacy showTitle={true} />} />
										<Route path="login" element={<SignInPage />} />
										{process.env.USER_BRANCH === 'dev' && !['dev.openpath.ai', 'preview.openpath.ai'].includes(window.location.hostname) ? (
											<Route path="sign_in" element={<ExternalRedirect href="https://dev.openpath.ai/sign_in" />} />
										) : (
											<Route path="sign_in" element={<SignInPage />} />
										)}
										<Route path="*" element={<Navigate to={{ pathname: 'login', search: location.search }} replace />} />
									</Routes>
								) : (
									<Routes>

										<Route path="/app/dashboard" element={<Home />} />

										{/* Customer Engagement + subpages*/}
										<Route path="/app/customers/*" element={<CustomerEngagement />}>
											<Route path="subjects" element={<Subjects />} />
											<Route path="studies" element={<Studies />} />
											<Route path="campaigns" element={<Campaigns />} />
											<Route path="CampaignList" element={<CampaignList />} />
											<Route path="data-segments" element={<DataSegments />} />
											<Route path="sites" element={<Sites />} />
											<Route path="recruitment-sources" element={<RecruitmentSources />} />
											<Route path="waitlists" element={<Waitlists />} />
											<Route path="workflows/*" element={<Workflows />} />



										</Route>



										<Route path="/app/ai" element={<AIAssistant />} />
					                    <Route path="/app/chatbot-studio" element={<ChatbotStudio />} />
										<Route path="/app/pipeline/*" element={<EnrollmentPipeline />} />
										{/*<Route path="/app/analytics" element={<Analytics />} />*/}
										<Route path="privacy" element={<Privacy showTitle={true} />} />
										<Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
										<Route path="*" element={<Navigate to={{ pathname: getCookie('DEFAULT_URL') || '/app/dashboard', search: location.search }} replace />} />
										<Route path="/campaigns" element={<CampaignList />} />
										<Route path="/campaigns/new" element={< Campaigns />} />

										{/*Routing for expanding pages*/}
										<Route path="/AgentActivityExpanded" element={<AgentActivityExpanded />} />
										<Route path="/CallAnalysisExpanded" element={<CallAnalysisExpanded />} />
										<Route path="/EngagementSummaryExpanded" element={<EngagementSummaryExpanded />} />
										<Route path="/TopOutreachMethodsExpended" element={<AgentActionQueueExpanded />} />
										<Route path="/TopPerformingOutreachMethodsExpanded" element={<TopPerformingOutreachMethodsExpanded />} />
										<Route path="/BookedRevenueExpanded" element={<BookedRevenueExpanded />} />
										<Route path="/app/workflows/:id" element={<FlowExpandedView />} />
					                    <Route path="/user-account/*" element={<UserAccountPage />} />

									</Routes>

								)}
							</div>
						</main>
						{/* <Footer /> */}
					</div>
				</div>
			</RootContext.Provider>
		</SidebarContext.Provider>
	);

};
